import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Wrench, Plus, X, Loader2, Edit3, Trash2, Search, Package, Building2,
  Users, CalendarClock, Zap, RefreshCw, Check, Tag, Boxes
} from "lucide-react";
import FilterMultiSelect, { SearchSelect } from "@/components/filters/FilterMultiSelect";

const FAMILLES = ["Froid", "Chaud", "Cuisine", "Eau", "Electricite", "IT", "Mobilier", "Securite", "Autres"];
const FREQ_OPTS = [
  { v: "mensuel", l: "Mensuel" },
  { v: "trimestriel", l: "Trimestriel" },
  { v: "semestriel", l: "Semestriel" },
  { v: "annuel", l: "Annuel" },
];
const STATUT_EQ = [
  { v: "ok", l: "OK", cls: "bg-emerald-100 text-emerald-700" },
  { v: "en_maintenance", l: "En maintenance", cls: "bg-blue-100 text-blue-700" },
  { v: "en_panne", l: "En panne", cls: "bg-amber-100 text-amber-700" },
  { v: "hors_service", l: "Hors service", cls: "bg-rose-100 text-rose-700" },
];

// ═════════════════════════════════════════════════════════════════════════
//  ROOT — 4-tab Admin > Maintenance
// ═════════════════════════════════════════════════════════════════════════
export default function AdminMaintenance() {
  const [tab, setTab] = useState("types");
  const tabs = [
    { k: "types", l: "Types d'intervention", icon: Tag },
    { k: "equipements", l: "Équipements", icon: Boxes },
    { k: "prestataires", l: "Prestataires", icon: Users },
    { k: "preventif", l: "Maintenances préventives", icon: CalendarClock },
  ];
  return (
    <div className="space-y-4" data-testid="admin-maintenance-page">
      <div className="flex flex-wrap gap-2 border-b border-slate-200 pb-2">
        {tabs.map(t => (
          <button key={t.k} onClick={() => setTab(t.k)} data-testid={`adm-maint-tab-${t.k}`}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border transition ${
                    tab === t.k ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
                  }`}>
            <t.icon className="w-4 h-4"/>{t.l}
          </button>
        ))}
      </div>
      {tab === "types" && <TypesPanel/>}
      {tab === "equipements" && <EquipementsPanel/>}
      {tab === "prestataires" && <PrestatairesPanel/>}
      {tab === "preventif" && <PreventifPanel/>}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════
//  TYPES D'INTERVENTION
// ═════════════════════════════════════════════════════════════════════════
function TypesPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [modal, setModal] = useState(null); // { mode:"new"|"edit", row? }

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/maintenance/types"); setRows(r.data || []); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Erreur"); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const filtered = rows.filter(r => !search || (r.label || "").toLowerCase().includes(search.toLowerCase()));

  const del = async (row) => {
    if (!window.confirm(`Supprimer le type « ${row.label} » ?`)) return;
    try { await api.delete(`/maintenance/types/${row.id}`); toast.success("Type supprimé"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Erreur"); }
  };

  return (
    <div className="space-y-3" data-testid="adm-maint-types">
      <PanelHeader
        title="Types d'intervention"
        subtitle="Ces types apparaissent à l'étape 4 du wizard de signalement (Maintenance)."
        search={search} setSearch={setSearch}
        onAdd={() => setModal({ mode: "new" })}
        addTestid="adm-type-add"
      />
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        {loading ? <div className="py-16 grid place-items-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin"/></div> : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-2.5">Libellé</th>
                <th className="text-left px-4 py-2.5">Famille rattachée</th>
                <th className="text-left px-4 py-2.5">Ordre</th>
                <th className="text-left px-4 py-2.5">Statut</th>
                <th className="text-right px-4 py-2.5">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(r => (
                <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`adm-type-row-${r.id}`}>
                  <td className="px-4 py-2.5 font-semibold text-slate-800">{r.label}</td>
                  <td className="px-4 py-2.5 text-slate-600">{r.famille || "—"}</td>
                  <td className="px-4 py-2.5 text-slate-500">{r.ordre ?? 100}</td>
                  <td className="px-4 py-2.5">
                    {r.actif !== false
                      ? <span className="text-[10px] font-bold uppercase bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">Actif</span>
                      : <span className="text-[10px] font-bold uppercase bg-slate-200 text-slate-500 px-2 py-0.5 rounded-full">Inactif</span>}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <IconBtn onClick={() => setModal({ mode: "edit", row: r })} testid={`adm-type-edit-${r.id}`}><Edit3 className="w-4 h-4"/></IconBtn>
                    <IconBtn onClick={() => del(r)} testid={`adm-type-del-${r.id}`}><Trash2 className="w-4 h-4 text-rose-500"/></IconBtn>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={5} className="py-10 text-center text-slate-400 text-sm">Aucun type</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>
      {modal && <TypeModal mode={modal.mode} row={modal.row} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }}/>}
    </div>
  );
}

function TypeModal({ mode, row, onClose, onSaved }) {
  const [form, setForm] = useState({
    label: row?.label || "",
    famille: row?.famille || "",
    ordre: row?.ordre ?? 100,
    actif: row?.actif !== false,
    description: row?.description || "",
  });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const body = { ...form, famille: form.famille || null, description: form.description || null };
      if (mode === "new") await api.post("/maintenance/types", body);
      else await api.patch(`/maintenance/types/${row.id}`, body);
      toast.success(mode === "new" ? "Type créé" : "Type mis à jour");
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Erreur"); }
    setSaving(false);
  };
  return (
    <ModalShell title={mode === "new" ? "Nouveau type d'intervention" : "Modifier le type"} onClose={onClose}>
      <form onSubmit={submit} className="p-5 space-y-3" data-testid="adm-type-modal">
        <Field label="Libellé *"><input required value={form.label} onChange={e => setForm({...form, label: e.target.value})} data-testid="adm-type-label" className="input"/></Field>
        <Field label="Famille (optionnelle)">
          <SearchSelect value={form.famille} onChange={v => setForm({...form, famille: v})} options={[...FAMILLES.map(f => ({ value: f, label: f }))]} placeholder={"Aucune"} data-testid="adm-type-famille" size="lg" menuWidth={260}/>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Ordre d'affichage"><input type="number" value={form.ordre} onChange={e => setForm({...form, ordre: parseInt(e.target.value)||0})} className="input"/></Field>
          <Field label="Statut">
            <label className="inline-flex items-center gap-2 text-sm mt-2"><input type="checkbox" checked={form.actif} onChange={e => setForm({...form, actif: e.target.checked})}/> Actif</label>
          </Field>
        </div>
        <Field label="Description (optionnelle)"><textarea rows={2} value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="input"/></Field>
        <ModalActions saving={saving} onClose={onClose} submitTestid="adm-type-save"/>
      </form>
    </ModalShell>
  );
}

// ═════════════════════════════════════════════════════════════════════════
//  ÉQUIPEMENTS
// ═════════════════════════════════════════════════════════════════════════
function EquipementsPanel() {
  const [rows, setRows] = useState([]);
  const [corners, setCorners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [famille, setFamille] = useState("");
  const [modal, setModal] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [r, c] = await Promise.all([
        api.get("/maintenance/equipements"),
        api.get("/maintenance/filter-options"),
      ]);
      setRows(r.data || []);
      setCorners(c.data?.corners || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Erreur"); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const filtered = rows.filter(r =>
    (!search || `${r.type||""} ${r.corner||""} ${r.marque||""} ${r.numero_serie||""}`.toLowerCase().includes(search.toLowerCase()))
    && (!famille || r.famille === famille)
  );

  return (
    <div className="space-y-3" data-testid="adm-maint-equipements">
      <PanelHeader
        title="Parc d'équipements"
        subtitle={`${rows.length} équipements dans le réseau. Ajout / édition manuelle depuis cette console.`}
        search={search} setSearch={setSearch}
        onAdd={() => setModal({ mode: "new" })}
        addTestid="adm-eq-add"
        extra={
          <SearchSelect value={famille} onChange={v => setFamille(v)} options={[...FAMILLES.map(f => ({ value: f, label: f }))]} placeholder={"Toutes familles"} data-testid="adm-eq-filter-famille" size="lg" menuWidth={260}/>
        }
      />
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        {loading ? <div className="py-16 grid place-items-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin"/></div> : (
          <div className="overflow-x-auto max-h-[70vh]">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider sticky top-0">
                <tr>
                  <th className="text-left px-3 py-2.5">Type</th>
                  <th className="text-left px-3 py-2.5">Corner</th>
                  <th className="text-left px-3 py-2.5">Famille</th>
                  <th className="text-left px-3 py-2.5">Marque</th>
                  <th className="text-left px-3 py-2.5">N° série</th>
                  <th className="text-left px-3 py-2.5">Statut</th>
                  <th className="text-right px-3 py-2.5">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.slice(0, 500).map(r => {
                  const s = STATUT_EQ.find(x => x.v === r.statut) || STATUT_EQ[0];
                  return (
                    <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`adm-eq-row-${r.id}`}>
                      <td className="px-3 py-2 font-semibold text-slate-800 whitespace-nowrap">{r.type}</td>
                      <td className="px-3 py-2 text-slate-600 whitespace-nowrap max-w-[220px] truncate">{r.corner || r.corner_key || "—"}</td>
                      <td className="px-3 py-2 text-slate-500">{r.famille}</td>
                      <td className="px-3 py-2 text-slate-500">{r.marque || "—"}</td>
                      <td className="px-3 py-2 text-slate-500 text-xs">{r.numero_serie || "—"}</td>
                      <td className="px-3 py-2"><span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${s.cls}`}>{s.l}</span></td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        <IconBtn onClick={() => setModal({ mode: "edit", row: r })} testid={`adm-eq-edit-${r.id}`}><Edit3 className="w-4 h-4"/></IconBtn>
                      </td>
                    </tr>
                  );
                })}
                {filtered.length === 0 && <tr><td colSpan={7} className="py-10 text-center text-slate-400 text-sm">Aucun équipement</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {modal && <EquipementModal mode={modal.mode} row={modal.row} corners={corners} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }}/>}
    </div>
  );
}

function EquipementModal({ mode, row, corners, onClose, onSaved }) {
  const [form, setForm] = useState({
    corner_key: row?.corner_key || "",
    famille: row?.famille || FAMILLES[0],
    type: row?.type || "",
    marque: row?.marque || "",
    modele: row?.modele || "",
    numero_serie: row?.numero_serie || "",
    date_installation: row?.date_installation || "",
    duree_garantie_mois: row?.duree_garantie_mois || "",
    statut: row?.statut || "ok",
    cout_achat: row?.cout_achat || "",
  });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const body = {
        ...form,
        duree_garantie_mois: form.duree_garantie_mois ? parseInt(form.duree_garantie_mois) : null,
        cout_achat: form.cout_achat ? parseFloat(form.cout_achat) : null,
      };
      if (mode === "new") await api.post("/maintenance/equipements", body);
      else await api.patch(`/maintenance/equipements/${row.id}`, body);
      toast.success(mode === "new" ? "Équipement créé" : "Équipement mis à jour");
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Erreur"); }
    setSaving(false);
  };
  return (
    <ModalShell title={mode === "new" ? "Nouvel équipement" : `Modifier — ${row?.type}`} onClose={onClose} wide>
      <form onSubmit={submit} className="p-5 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="adm-eq-modal">
        <Field label="Corner *">
          <SearchSelect value={form.corner_key} onChange={v => setForm({...form, corner_key: v})} options={[...corners.map(c => ({ value: c.corner_key, label: c.corner || c.corner_key }))]} placeholder={"— Choisir —"} required data-testid="adm-eq-corner" size="lg" menuWidth={260}/>
        </Field>
        <Field label="Famille *">
          <SearchSelect value={form.famille} onChange={v => setForm({...form, famille: v})} options={[...FAMILLES.map(f => ({ value: f, label: f }))]} required size="lg" menuWidth={260}/>
        </Field>
        <Field label="Type / désignation *"><input required value={form.type} onChange={e => setForm({...form, type: e.target.value})} className="input" data-testid="adm-eq-type"/></Field>
        <Field label="Statut">
          <SearchSelect value={form.statut} onChange={v => setForm({...form, statut: v})} options={[...STATUT_EQ.map(s => ({ value: s.v, label: s.l }))]} size="lg" menuWidth={260}/>
        </Field>
        <Field label="Marque"><input value={form.marque} onChange={e => setForm({...form, marque: e.target.value})} className="input"/></Field>
        <Field label="Modèle"><input value={form.modele} onChange={e => setForm({...form, modele: e.target.value})} className="input"/></Field>
        <Field label="N° série"><input value={form.numero_serie} onChange={e => setForm({...form, numero_serie: e.target.value})} className="input"/></Field>
        <Field label="Date d'installation"><input type="date" value={form.date_installation} onChange={e => setForm({...form, date_installation: e.target.value})} className="input"/></Field>
        <Field label="Garantie (mois)"><input type="number" value={form.duree_garantie_mois} onChange={e => setForm({...form, duree_garantie_mois: e.target.value})} className="input"/></Field>
        <Field label="Coût d'achat (€)"><input type="number" step="0.01" value={form.cout_achat} onChange={e => setForm({...form, cout_achat: e.target.value})} className="input"/></Field>
        <div className="col-span-full"><ModalActions saving={saving} onClose={onClose} submitTestid="adm-eq-save"/></div>
      </form>
    </ModalShell>
  );
}

// ═════════════════════════════════════════════════════════════════════════
//  PRESTATAIRES
// ═════════════════════════════════════════════════════════════════════════
function PrestatairesPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [modal, setModal] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/maintenance/prestataires"); setRows(r.data || []); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Erreur"); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const filtered = rows.filter(r => !search || `${r.nom||""} ${r.ville||""} ${(r.familles||[]).join(",")}`.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="space-y-3" data-testid="adm-maint-prestataires">
      <PanelHeader
        title="Prestataires"
        subtitle="Fournisseurs et techniciens externes disponibles pour affectation."
        search={search} setSearch={setSearch}
        onAdd={() => setModal({ mode: "new" })}
        addTestid="adm-prest-add"
      />
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {loading ? <div className="col-span-full py-16 grid place-items-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin"/></div> : filtered.map(p => (
          <div key={p.id} className="bg-white border border-slate-200 rounded-2xl p-4" data-testid={`adm-prest-card-${p.id}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="font-display font-bold text-slate-900">{p.nom}</div>
                <div className="text-[11px] text-slate-500 mt-0.5">{p.ville || "—"} · {p.contrat_actif ? "Contrat actif" : "Ponctuel"}</div>
              </div>
              <button onClick={() => setModal({ mode: "edit", row: p })} data-testid={`adm-prest-edit-${p.id}`} className="w-8 h-8 grid place-items-center rounded-lg hover:bg-slate-100"><Edit3 className="w-4 h-4"/></button>
            </div>
            <div className="flex flex-wrap gap-1 mt-2">
              {(p.familles || []).map(f => <span key={f} className="text-[10px] font-semibold uppercase bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{f}</span>)}
            </div>
            <div className="mt-3 text-xs text-slate-600 space-y-0.5">
              <div>👤 {p.contact_nom || "—"}</div>
              <div>✉ {p.contact_email || "—"}</div>
              <div>📞 {p.contact_tel || "—"}</div>
              <div className="pt-1 border-t border-slate-100 mt-1 flex gap-3 text-[11px]">
                {p.tarif_horaire && <span>💶 {p.tarif_horaire}€/h</span>}
                {p.delai_intervention_h && <span>⏱ {p.delai_intervention_h}h</span>}
                {p.note != null && <span>⭐ {p.note}</span>}
              </div>
            </div>
          </div>
        ))}
        {(!loading && filtered.length === 0) && <div className="col-span-full py-10 text-center text-slate-400 text-sm">Aucun prestataire</div>}
      </div>
      {modal && <PrestataireModal mode={modal.mode} row={modal.row} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }}/>}
    </div>
  );
}

function PrestataireModal({ mode, row, onClose, onSaved }) {
  const [form, setForm] = useState({
    nom: row?.nom || "",
    familles: row?.familles || [],
    contact_nom: row?.contact_nom || "",
    contact_email: row?.contact_email || "",
    contact_tel: row?.contact_tel || "",
    ville: row?.ville || "",
    zones: row?.zones || [],
    tarif_horaire: row?.tarif_horaire || "",
    delai_intervention_h: row?.delai_intervention_h || "",
    note: row?.note || "",
    contrat_actif: row?.contrat_actif !== false,
    commentaire: row?.commentaire || "",
  });
  const [saving, setSaving] = useState(false);
  const toggleFamille = (f) => setForm(s => ({ ...s, familles: s.familles.includes(f) ? s.familles.filter(x => x !== f) : [...s.familles, f] }));
  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const body = {
        ...form,
        tarif_horaire: form.tarif_horaire ? parseFloat(form.tarif_horaire) : null,
        delai_intervention_h: form.delai_intervention_h ? parseInt(form.delai_intervention_h) : null,
        note: form.note ? parseFloat(form.note) : null,
      };
      if (mode === "new") await api.post("/maintenance/prestataires", body);
      else await api.patch(`/maintenance/prestataires/${row.id}`, body);
      toast.success(mode === "new" ? "Prestataire créé" : "Prestataire mis à jour");
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Erreur"); }
    setSaving(false);
  };
  return (
    <ModalShell title={mode === "new" ? "Nouveau prestataire" : `Modifier — ${row?.nom}`} onClose={onClose} wide>
      <form onSubmit={submit} className="p-5 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="adm-prest-modal">
        <Field label="Nom *"><input required value={form.nom} onChange={e => setForm({...form, nom: e.target.value})} className="input" data-testid="adm-prest-nom"/></Field>
        <Field label="Ville"><input value={form.ville} onChange={e => setForm({...form, ville: e.target.value})} className="input"/></Field>
        <div className="col-span-full">
          <Field label="Familles couvertes">
            <div className="flex flex-wrap gap-1.5">
              {FAMILLES.map(f => (
                <button type="button" key={f} onClick={() => toggleFamille(f)}
                        data-testid={`adm-prest-fam-${f}`}
                        className={`text-xs font-semibold px-3 py-1.5 rounded-full border ${form.familles.includes(f) ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-200"}`}>{f}</button>
              ))}
            </div>
          </Field>
        </div>
        <Field label="Contact — Nom"><input value={form.contact_nom} onChange={e => setForm({...form, contact_nom: e.target.value})} className="input"/></Field>
        <Field label="Contact — Email"><input type="email" value={form.contact_email} onChange={e => setForm({...form, contact_email: e.target.value})} className="input"/></Field>
        <Field label="Contact — Téléphone"><input value={form.contact_tel} onChange={e => setForm({...form, contact_tel: e.target.value})} className="input"/></Field>
        <Field label="Zones d'intervention (séparées par une virgule)">
          <input value={(form.zones || []).join(", ")} onChange={e => setForm({...form, zones: e.target.value.split(",").map(x => x.trim()).filter(Boolean)})} className="input"/>
        </Field>
        <Field label="Tarif horaire (€/h)"><input type="number" step="0.01" value={form.tarif_horaire} onChange={e => setForm({...form, tarif_horaire: e.target.value})} className="input"/></Field>
        <Field label="Délai d'intervention (h)"><input type="number" value={form.delai_intervention_h} onChange={e => setForm({...form, delai_intervention_h: e.target.value})} className="input"/></Field>
        <Field label="Note (0–5)"><input type="number" step="0.1" min="0" max="5" value={form.note} onChange={e => setForm({...form, note: e.target.value})} className="input"/></Field>
        <Field label="Contrat"><label className="inline-flex items-center gap-2 text-sm mt-2"><input type="checkbox" checked={form.contrat_actif} onChange={e => setForm({...form, contrat_actif: e.target.checked})}/> Contrat actif</label></Field>
        <div className="col-span-full"><Field label="Commentaire"><textarea rows={2} value={form.commentaire} onChange={e => setForm({...form, commentaire: e.target.value})} className="input"/></Field></div>
        <div className="col-span-full"><ModalActions saving={saving} onClose={onClose} submitTestid="adm-prest-save"/></div>
      </form>
    </ModalShell>
  );
}

// ═════════════════════════════════════════════════════════════════════════
//  PLANS PRÉVENTIFS
// ═════════════════════════════════════════════════════════════════════════
function PreventifPanel() {
  const [rows, setRows] = useState([]);
  const [corners, setCorners] = useState([]);
  const [prests, setPrests] = useState([]);
  const [equipements, setEquipements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [modal, setModal] = useState(null);
  const [generating, setGenerating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [r, c, p, eq] = await Promise.all([
        api.get("/maintenance/preventif"),
        api.get("/maintenance/filter-options"),
        api.get("/maintenance/prestataires"),
        api.get("/maintenance/equipements"),
      ]);
      setRows(r.data || []); setCorners(c.data?.corners || []);
      setPrests(p.data || []); setEquipements(eq.data || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Erreur"); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const filtered = rows.filter(r => !search || (r.intitule || "").toLowerCase().includes(search.toLowerCase()));

  const genOne = async (row) => {
    if (!window.confirm(`Générer maintenant les interventions du plan « ${row.intitule} » ?\nCela créera automatiquement une intervention (P3, source Maintenance préventive) dans Support > Maintenance ET dans Centre des opérations > Interventions, pour chaque corner ciblé.`)) return;
    try {
      const r = await api.post(`/maintenance/preventif/${row.id}/generate`);
      toast.success(`${r.data.generated} interventions générées · prochaine échéance ${r.data.prochaine_echeance}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Erreur"); }
  };

  const genDue = async () => {
    setGenerating(true);
    try {
      const r = await api.post("/maintenance/preventif/generate-due");
      toast.success(`${r.data.plans_processed} plans traités · ${r.data.interventions_generated} interventions créées`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Erreur"); }
    setGenerating(false);
  };

  const del = async (row) => {
    if (!window.confirm(`Supprimer le plan préventif « ${row.intitule} » ?`)) return;
    try { await api.delete(`/maintenance/preventif/${row.id}`); toast.success("Plan supprimé"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Erreur"); }
  };

  const cornerLabel = (r) => {
    if (r.apply_to_all) return "🌐 Tous les corners";
    const cks = [...(r.corner_keys || [])];
    if (r.corner_key && !cks.includes(r.corner_key)) cks.push(r.corner_key);
    if (cks.length === 0) return "—";
    if (cks.length === 1) return corners.find(c => c.corner_key === cks[0])?.corner || cks[0];
    return `${cks.length} corners`;
  };

  return (
    <div className="space-y-3" data-testid="adm-maint-preventif">
      <PanelHeader
        title="Maintenances préventives"
        subtitle="Plans récurrents. À l'échéance, l'intervention est créée automatiquement dans Support > Maintenance ET dans Centre des opérations > Interventions."
        search={search} setSearch={setSearch}
        onAdd={() => setModal({ mode: "new" })}
        addTestid="adm-prev-add"
        extra={
          <button onClick={genDue} disabled={generating} data-testid="adm-prev-run-due"
                  className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold border border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100 disabled:opacity-50">
            {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Zap className="w-3.5 h-3.5"/>} Générer les échéances
          </button>
        }
      />
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        {loading ? <div className="py-16 grid place-items-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin"/></div> : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-3 py-2.5">Intitulé</th>
                <th className="text-left px-3 py-2.5">Périmètre</th>
                <th className="text-left px-3 py-2.5">Fréquence</th>
                <th className="text-left px-3 py-2.5">Prochaine échéance</th>
                <th className="text-left px-3 py-2.5">Prestataire</th>
                <th className="text-left px-3 py-2.5">Statut</th>
                <th className="text-right px-3 py-2.5">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(r => {
                const late = r.prochaine_echeance && r.prochaine_echeance < new Date().toISOString().slice(0, 10);
                return (
                  <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`adm-prev-row-${r.id}`}>
                    <td className="px-3 py-2 font-semibold text-slate-800">{r.intitule}</td>
                    <td className="px-3 py-2 text-slate-600 max-w-[220px] truncate">{cornerLabel(r)}</td>
                    <td className="px-3 py-2 text-slate-500 capitalize">{r.frequence}</td>
                    <td className={`px-3 py-2 text-slate-600 ${late ? "text-rose-600 font-bold" : ""}`}>{r.prochaine_echeance || "—"}{late && " ⚠"}</td>
                    <td className="px-3 py-2 text-slate-500 text-xs">{r.prestataire_nom || "—"}</td>
                    <td className="px-3 py-2">
                      {r.actif !== false
                        ? <span className="text-[10px] font-bold uppercase bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">Actif</span>
                        : <span className="text-[10px] font-bold uppercase bg-slate-200 text-slate-500 px-2 py-0.5 rounded-full">Inactif</span>}
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <IconBtn onClick={() => genOne(r)} testid={`adm-prev-gen-${r.id}`} title="Générer maintenant"><Zap className="w-4 h-4 text-amber-600"/></IconBtn>
                      <IconBtn onClick={() => setModal({ mode: "edit", row: r })} testid={`adm-prev-edit-${r.id}`}><Edit3 className="w-4 h-4"/></IconBtn>
                      <IconBtn onClick={() => del(r)} testid={`adm-prev-del-${r.id}`}><Trash2 className="w-4 h-4 text-rose-500"/></IconBtn>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && <tr><td colSpan={7} className="py-10 text-center text-slate-400 text-sm">Aucun plan préventif</td></tr>}
            </tbody>
          </table>
        )}
      </div>
      {modal && <PreventifModal mode={modal.mode} row={modal.row} corners={corners} prests={prests} equipements={equipements} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }}/>}
    </div>
  );
}

function PreventifModal({ mode, row, corners, prests, equipements, onClose, onSaved }) {
  const [form, setForm] = useState({
    intitule: row?.intitule || "",
    apply_to_all: row?.apply_to_all || false,
    corner_keys: row?.corner_keys || (row?.corner_key ? [row.corner_key] : []),
    equipement_id: row?.equipement_id || "",
    famille: row?.famille || "",
    frequence: row?.frequence || "trimestriel",
    prochaine_echeance: row?.prochaine_echeance || new Date().toISOString().slice(0, 10),
    prestataire_id: row?.prestataire_id || "",
    duree_estimee_h: row?.duree_estimee_h || "",
    cout_estime: row?.cout_estime || "",
    actif: row?.actif !== false,
  });
  const [saving, setSaving] = useState(false);
  const [cornerSearch, setCornerSearch] = useState("");
  const filteredCorners = corners.filter(c => !cornerSearch || (c.corner || c.corner_key).toLowerCase().includes(cornerSearch.toLowerCase()));

  const toggleCorner = (ck) => setForm(s => ({
    ...s,
    corner_keys: s.corner_keys.includes(ck) ? s.corner_keys.filter(x => x !== ck) : [...s.corner_keys, ck],
  }));

  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const prestObj = prests.find(p => p.id === form.prestataire_id);
      const eqObj = equipements.find(x => x.id === form.equipement_id);
      const body = {
        intitule: form.intitule,
        apply_to_all: form.apply_to_all,
        corner_keys: form.apply_to_all ? [] : form.corner_keys,
        corner_key: (!form.apply_to_all && form.corner_keys.length === 1) ? form.corner_keys[0] : null,
        equipement_id: form.equipement_id || null,
        equipement_label: eqObj?.type || null,
        famille: form.famille || eqObj?.famille || null,
        frequence: form.frequence,
        prochaine_echeance: form.prochaine_echeance,
        prestataire_id: form.prestataire_id || null,
        prestataire_nom: prestObj?.nom || null,
        duree_estimee_h: form.duree_estimee_h ? parseFloat(form.duree_estimee_h) : null,
        cout_estime: form.cout_estime ? parseFloat(form.cout_estime) : null,
        actif: form.actif,
      };
      if (mode === "new") await api.post("/maintenance/preventif", body);
      else await api.patch(`/maintenance/preventif/${row.id}`, body);
      toast.success(mode === "new" ? "Plan préventif créé" : "Plan préventif mis à jour");
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Erreur"); }
    setSaving(false);
  };

  return (
    <ModalShell title={mode === "new" ? "Nouveau plan préventif" : `Modifier — ${row?.intitule}`} onClose={onClose} wide>
      <form onSubmit={submit} className="p-5 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="adm-prev-modal">
        <div className="col-span-full"><Field label="Intitulé *"><input required value={form.intitule} onChange={e => setForm({...form, intitule: e.target.value})} className="input" placeholder="ex. Entretien trimestriel vitrines froides" data-testid="adm-prev-intitule"/></Field></div>
        <Field label="Fréquence *">
          <SearchSelect value={form.frequence} onChange={v => setForm({...form, frequence: v})} options={[...FREQ_OPTS.map(f => ({ value: f.v, label: f.l }))]} size="lg" menuWidth={260}/>
        </Field>
        <Field label="Prochaine échéance *"><input type="date" required value={form.prochaine_echeance} onChange={e => setForm({...form, prochaine_echeance: e.target.value})} className="input"/></Field>
        <Field label="Famille (optionnelle)">
          <SearchSelect value={form.famille} onChange={v => setForm({...form, famille: v})} options={[...FAMILLES.map(f => ({ value: f, label: f }))]} placeholder={"Aucune"} size="lg" menuWidth={260}/>
        </Field>
        <Field label="Équipement (optionnel)">
          <SearchSelect value={form.equipement_id} onChange={v => setForm({...form, equipement_id: v})} options={[...equipements.slice(0, 500).map(e => ({ value: e.id, label: `${e.type} — ${e.corner || e.corner_key}` }))]} placeholder={"Aucun"} size="lg" menuWidth={260}/>
        </Field>
        <Field label="Prestataire">
          <SearchSelect value={form.prestataire_id} onChange={v => setForm({...form, prestataire_id: v})} options={[...prests.map(p => ({ value: p.id, label: p.nom }))]} placeholder={"Aucun"} size="lg" menuWidth={260}/>
        </Field>
        <Field label="Durée estimée (h)"><input type="number" step="0.5" value={form.duree_estimee_h} onChange={e => setForm({...form, duree_estimee_h: e.target.value})} className="input"/></Field>
        <Field label="Coût estimé (€)"><input type="number" step="0.01" value={form.cout_estime} onChange={e => setForm({...form, cout_estime: e.target.value})} className="input"/></Field>
        <Field label="Actif"><label className="inline-flex items-center gap-2 text-sm mt-2"><input type="checkbox" checked={form.actif} onChange={e => setForm({...form, actif: e.target.checked})}/> Plan actif</label></Field>

        <div className="col-span-full border-t border-slate-100 pt-3 mt-1">
          <label className="text-[11px] font-bold uppercase tracking-wider text-slate-600 flex items-center gap-2 mb-2">
            <Building2 className="w-3.5 h-3.5"/> Périmètre d'application
          </label>
          <label className="flex items-center gap-2 text-sm mb-2 font-semibold">
            <input type="checkbox" checked={form.apply_to_all} onChange={e => setForm({...form, apply_to_all: e.target.checked})} data-testid="adm-prev-apply-all"/>
            Appliquer à tous les corners du réseau
          </label>
          {!form.apply_to_all && (
            <>
              <div className="text-xs text-slate-500 mb-1">Sélectionne un ou plusieurs corners cibles :</div>
              <input value={cornerSearch} onChange={e => setCornerSearch(e.target.value)} placeholder="Rechercher un corner…" className="input mb-2" data-testid="adm-prev-corner-search"/>
              <div className="max-h-48 overflow-y-auto border border-slate-200 rounded-lg p-2 space-y-0.5" data-testid="adm-prev-corner-list">
                {filteredCorners.slice(0, 200).map(c => (
                  <label key={c.corner_key} className="flex items-center gap-2 text-xs py-0.5 px-1 rounded hover:bg-slate-50 cursor-pointer">
                    <input type="checkbox" checked={form.corner_keys.includes(c.corner_key)} onChange={() => toggleCorner(c.corner_key)}/>
                    <span className="truncate">{c.corner || c.corner_key}</span>
                    {c.dr && <span className="text-slate-400 ml-auto text-[10px]">{c.dr}</span>}
                  </label>
                ))}
                {filteredCorners.length === 0 && <div className="text-center text-slate-400 text-xs py-4">Aucun corner</div>}
              </div>
              <div className="text-[11px] text-slate-500 mt-1">{form.corner_keys.length} corner(s) sélectionné(s)</div>
            </>
          )}
        </div>
        <div className="col-span-full"><ModalActions saving={saving} onClose={onClose} submitTestid="adm-prev-save"/></div>
      </form>
    </ModalShell>
  );
}

// ═════════════════════════════════════════════════════════════════════════
//  SHARED UI
// ═════════════════════════════════════════════════════════════════════════
function PanelHeader({ title, subtitle, search, setSearch, onAdd, addTestid, extra }) {
  return (
    <div className="flex items-center justify-between flex-wrap gap-3">
      <div>
        <h2 className="font-display text-xl font-bold text-slate-900">{title}</h2>
        <p className="text-slate-500 text-sm mt-0.5">{subtitle}</p>
      </div>
      <div className="flex items-center gap-2">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher…"
                 className="pl-9 pr-3 py-2 rounded-xl border border-slate-200 bg-white text-sm outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400 w-56"/>
        </div>
        {extra}
        <button onClick={onAdd} data-testid={addTestid}
                className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-bold bg-blue-600 hover:bg-blue-700 text-white">
          <Plus className="w-4 h-4"/> Ajouter
        </button>
      </div>
    </div>
  );
}

function IconBtn({ onClick, children, testid, title }) {
  return (
    <button onClick={onClick} data-testid={testid} title={title}
            className="w-8 h-8 grid place-items-center rounded-lg hover:bg-slate-100 inline-flex ml-1">
      {children}
    </button>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[11px] font-bold uppercase tracking-wider text-slate-600 block mb-1">{label}</span>
      {children}
    </label>
  );
}

function ModalShell({ title, onClose, wide, children }) {
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm grid place-items-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()}
           className={`bg-white rounded-2xl w-full ${wide ? "max-w-2xl" : "max-w-md"} border border-slate-200 shadow-xl max-h-[90vh] overflow-y-auto`}>
        <div className="sticky top-0 flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-white z-10">
          <h2 className="font-display text-lg font-semibold">{title}</h2>
          <button type="button" onClick={onClose} className="w-8 h-8 grid place-items-center rounded-lg hover:bg-slate-100"><X className="w-4 h-4"/></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function ModalActions({ saving, onClose, submitTestid }) {
  return (
    <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
      <button type="button" onClick={onClose} className="px-4 py-2 rounded-xl text-sm font-semibold text-slate-600 hover:bg-slate-100">Annuler</button>
      <button type="submit" disabled={saving} data-testid={submitTestid} className="px-4 py-2 rounded-xl text-sm font-semibold bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-60 inline-flex items-center gap-1.5">
        {saving && <Loader2 className="w-3.5 h-3.5 animate-spin"/>} Enregistrer
      </button>
    </div>
  );
}

// Tailwind convenience for inputs (auto-applies to all children through class ".input")
if (typeof document !== "undefined" && !document.getElementById("adm-maint-input-css")) {
  const st = document.createElement("style"); st.id = "adm-maint-input-css";
  st.textContent = `.input{width:100%;font-size:.875rem;padding:.5rem .75rem;border:1px solid rgb(226 232 240);border-radius:.5rem;outline:none;background:#fff;}
.input:focus{border-color:rgb(96 165 250);box-shadow:0 0 0 3px rgb(219 234 254);}`;
  document.head.appendChild(st);
}
